/**
 * API Client
 *
 * Centralized HTTP client for all backend API calls.
 * Handles authentication headers, error handling, and response validation.
 */

import axios, { type AxiosInstance, type AxiosError } from 'axios';
import { z } from 'zod';
import type {
  LoginRequest,
  LoginResponse,
  NewPasswordRequest,
  NewPasswordResponse,
  ForgotPasswordRequest,
  ConfirmForgotPasswordRequest,
  MessageResponse,
} from '@/types/auth';
import type {
  AuditLog,
  AuditLogCategory,
  AuditLogFilters,
  PaginatedAuditLogs,
  AuditLogSummary,
} from '@/types/audit-log';
import type {
  MigrationUser,
  ListMigrationUsersResponse,
  ListMigrationUsersParams,
  CreateMigrationUserRequest,
  UpdateMigrationUserRequest,
  DeleteMigrationUserResponse,
  SubmitImportResponse,
  ImportJobStatusResponse,
  CancelImportResponse,
  IdempotentImportResponse,
} from '@/types/user-migration';
import type { AnalyticsSummary, AnalyticsTimeRange } from '@/types/analytics';
import type {
  UserListParams,
  UserListResponse,
  UserDetailsResponse,
  UpdateUserRequest,
  UpdateUserResponse,
  OloLinkResponse,
  PunchhAccountInfo,
  CreatePunchhAccountResponse,
  RegeneratePunchhTokensResponse,
  ForceSignOutResponse,
  ExpireIdTokenResponse,
} from '@/types/user-management';
import type {
  CacheOverviewResponse,
  CacheNamespace,
  InvalidateResponse,
  BulkInvalidateResponse,
  NamespaceStatusResponse,
  NamespaceKeysResponse,
  DeleteCacheKeyResponse,
} from '@/types/cache';
import type {
  GroupOrderSnapshot,
  ExpireGroupOrderBody,
  GroupOrderSweepBody,
  ExpirySweepSummary,
  HostReminderSweepSummary,
  ShortenDeadlineBody,
} from '@/types/group-order';
import { CLIENT_TYPES } from '@/types/api-key';
import { CACHE_NAMESPACES } from '@/types/cache';
import type { GlobalConfigResponse } from '@/types/global-config';
import {
  EMAIL_MATCH_MODES,
  FEATURE_FLAG_GATE_PLATFORMS,
  PLATFORM_TAGS,
  VERSION_OPERATORS,
  type ArchiveFeatureFlagResponse,
  type ClearFlagGateResponse,
  type CreateFeatureFlagBody,
  type DeleteFlagRuleResponse,
  type FeatureFlagDefinition,
  type FeatureFlagGatePlatform,
  type FeatureFlagsResponse,
  type FlagGatesResponse,
  type FlagRuleBody,
  type FlagRuleResponse,
  type FlagRulesResponse,
  type FlagScope,
  type UpdateFlagRuleBody,
  type UpdateFeatureFlagBody,
  type UpdateFeatureFlagResponse,
  type UpsertFlagGateBody,
  type UpsertFlagGateResponse,
} from '@/types/feature-flag';
import type {
  TenantFeatureFlagsResponse,
  UpdateTenantFeatureFlagBody,
  UpdateTenantFeatureFlagResponse,
} from '@/types/tenant-feature-flag';
import type {
  ArchiveStatusResponse,
  TriggerArchiveRequest,
  TriggerArchiveResponse,
  ArchivePreviewResponse,
} from '@/types/archive-management';
import { config } from '@/config/env';

// Environment variables
const API_BASE_URL = config.api.baseUrl;
const ADMIN_API_BASE_URL = config.api.adminBaseUrl;

/**
 * Axios instance configured for the audit logs API
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Platform': 'backend',
  },
});

/**
 * Error Response Schema
 * Validates backend error responses
 */
const ErrorResponseSchema = z.object({
  message: z.string(),
  error: z.string().optional(),
  statusCode: z.number().optional(),
});

/**
 * API Error Class
 * Custom error class for API-related errors
 */
export class ApiError extends Error {
  statusCode?: number;
  errorCode?: string;

  constructor(message: string, statusCode?: number, errorCode?: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
  }
}

/**
 * Handle API Errors
 * Converts axios errors to ApiError instances with proper error messages
 *
 * @param error - The error from axios
 * @throws {ApiError} Formatted API error
 */
function handleApiError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;

    // Network or timeout error
    if (!axiosError.response) {
      throw new ApiError(
        'Network error. Please check your connection and try again.',
        0,
        'NETWORK_ERROR'
      );
    }

    // Server returned error response
    const { status, data } = axiosError.response;

    // Try to parse error response. Use safeParse so a successful parse doesn't
    // get caught by the fallback below — otherwise the real server message would
    // be silently replaced with a generic one.
    const parsedError = ErrorResponseSchema.safeParse(data);
    if (parsedError.success) {
      throw new ApiError(
        parsedError.data.message || 'An unexpected error occurred',
        status,
        parsedError.data.error
      );
    }

    // Parsing failed — fall back to a generic message
    throw new ApiError(`Request failed with status ${status}`, status, 'UNKNOWN_ERROR');
  }

  // Handle Zod validation errors
  if (error instanceof z.ZodError) {
    const firstError = error.errors[0];
    const errorMessage = firstError
      ? `Validation error: ${firstError.path.join('.')} - ${firstError.message}`
      : 'Response validation failed';
    throw new ApiError(errorMessage, undefined, 'VALIDATION_ERROR');
  }

  // Non-axios error
  throw new ApiError('An unexpected error occurred', undefined, 'UNKNOWN_ERROR');
}

/**
 * Set Authorization Header
 * Adds JWT access token to axios instance default headers
 *
 * @param token - Access token
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
}

/**
 * Login API Schema
 * Validates login response from backend
 */
const LoginResponseSchema = z.object({
  challengeName: z.literal('NEW_PASSWORD_REQUIRED').optional(),
  session: z.string().optional(),
  authenticationResult: z
    .object({
      accessToken: z.string(),
      idToken: z.string(),
      refreshToken: z.string(),
      expiresIn: z.number(),
    })
    .optional(),
  userAttributes: z.record(z.string()).optional(),
});

/**
 * New Password Response Schema
 * Validates new password challenge response
 */
const NewPasswordResponseSchema = z.object({
  authenticationResult: z.object({
    accessToken: z.string(),
    idToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
  }),
});

/**
 * Login
 * Authenticate user with username and password
 *
 * @param credentials - Username and password
 * @returns Login response with tokens or challenge
 * @throws {ApiError} If login fails
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  try {
    const response = await apiClient.post('/v1/admin/dashboard/auth/login', credentials, {
      baseURL: ADMIN_API_BASE_URL,
    });

    // Backend wraps response in { message, data } structure
    // Extract the actual data payload
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = LoginResponseSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Respond to New Password Challenge
 * Complete NEW_PASSWORD_REQUIRED challenge with new password
 *
 * @param request - New password request with session
 * @returns Authentication result with tokens
 * @throws {ApiError} If password change fails
 */
export async function respondToNewPassword(
  request: NewPasswordRequest
): Promise<NewPasswordResponse> {
  try {
    const response = await apiClient.post('/v1/admin/dashboard/auth/respond-new-password', request, {
      baseURL: ADMIN_API_BASE_URL,
    });

    // Backend wraps response in { message, data } structure
    // Extract the actual data payload
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = NewPasswordResponseSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Message Response Schema
 * Validates the `{ message }` payload returned by the forgot-password endpoints.
 * The backend wraps responses in `{ message, data }`; only `message` is meaningful here.
 */
const MessageResponseSchema = z.object({
  message: z.string(),
});

/**
 * Forgot Password
 * Initiates the password reset flow — Cognito emails a verification code.
 *
 * Note: the admin user pool has `preventUserExistenceErrors` enabled, so this
 * resolves successfully whether or not the email maps to a real account.
 *
 * @param request - Email of the account to reset
 * @returns Success message
 * @throws {ApiError} If the request fails
 */
export async function forgotPassword(request: ForgotPasswordRequest): Promise<MessageResponse> {
  try {
    const response = await apiClient.post('/v1/admin/dashboard/auth/forgot-password', request, {
      baseURL: ADMIN_API_BASE_URL,
    });
    return MessageResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Confirm Forgot Password
 * Completes the password reset using the emailed verification code and a new password.
 *
 * @param request - Email, confirmation code, and new password
 * @returns Success message
 * @throws {ApiError} If the request fails (e.g. invalid/expired code, weak password)
 */
export async function confirmForgotPassword(
  request: ConfirmForgotPasswordRequest
): Promise<MessageResponse> {
  try {
    const response = await apiClient.post(
      '/v1/admin/dashboard/auth/confirm-forgot-password',
      request,
      { baseURL: ADMIN_API_BASE_URL }
    );
    return MessageResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Audit Logs Response Schema
 * Validates paginated audit logs response
 */
const PaginatedAuditLogsSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      eventType: z.string(),
      category: z.enum([
        'authentication',
        'authorization',
        'user_profile',
        'restaurant',
        'basket',
        'order',
        'payment',
        'loyalty',
        'vehicle',
        'admin',
        'system',
        'product',
      ]),
      severity: z.enum(['info', 'warning', 'error', 'critical']),
      userId: z.string().nullable().optional(),
      adminId: z.string().nullable().optional(),
      ipAddress: z.string(),
      userAgent: z.string().nullable().optional(),
      resourceType: z.string().nullable().optional(),
      resourceId: z.string().nullable().optional(),
      action: z.string(),
      httpMethod: z.string().nullable().optional(),
      endpoint: z.string().nullable().optional(),
      statusCode: z.number().nullable().optional(),
      responseTimeMs: z.number().nullable().optional(),
      requestData: z.record(z.unknown()).nullable().optional(),
      responseData: z.record(z.unknown()).nullable().optional(),
      metadata: z.record(z.unknown()).nullable().optional(),
      oldValue: z.record(z.unknown()).nullable().optional(),
      newValue: z.record(z.unknown()).nullable().optional(),
      sessionId: z.string().nullable().optional(),
      correlationId: z.string(),
      occurredAt: z.string(), // ISO 8601 date string
      archivedAt: z.string().nullable().optional(),
      cacheStatus: z.enum(['hit', 'miss']).nullable().optional(),
    })
  ),
  pagination: z.object({
    total: z.number(),
    page: z.coerce.number(),
    limit: z.coerce.number(),
    totalPages: z.number(),
  }),
  // severityCounts removed — now comes from AuditLogSummarySchema via /summary endpoint
});

/**
 * Audit Log Summary Response Schema
 * Validates the response from GET /summary
 */
const AuditLogSummarySchema = z.object({
  total: z.number(),
  // Default false so the UI keeps working against a backend that predates capping
  isCapped: z.boolean().default(false),
  severityCounts: z.object({
    info: z.number(),
    warning: z.number(),
    error: z.number(),
    critical: z.number(),
  }),
});

/**
 * Single Audit Log Response Schema
 */
const AuditLogSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  category: z.enum([
    'authentication',
    'authorization',
    'user_profile',
    'restaurant',
    'basket',
    'order',
    'payment',
    'loyalty',
    'vehicle',
    'admin',
    'system',
    'product',
  ]),
  severity: z.enum(['info', 'warning', 'error', 'critical']),
  userId: z.string().nullable().optional(),
  adminId: z.string().nullable().optional(),
  ipAddress: z.string(),
  userAgent: z.string().nullable().optional(),
  resourceType: z.string().nullable().optional(),
  resourceId: z.string().nullable().optional(),
  action: z.string(),
  httpMethod: z.string().nullable().optional(),
  endpoint: z.string().nullable().optional(),
  statusCode: z.number().nullable().optional(),
  responseTimeMs: z.number().nullable().optional(),
  requestData: z.record(z.unknown()).nullable().optional(),
  responseData: z.record(z.unknown()).nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  oldValue: z.record(z.unknown()).nullable().optional(),
  newValue: z.record(z.unknown()).nullable().optional(),
  sessionId: z.string().nullable().optional(),
  correlationId: z.string(),
  occurredAt: z.string(),
  archivedAt: z.string().nullable().optional(),
  cacheStatus: z.enum(['hit', 'miss']).nullable().optional(),
});

/**
 * Build Query String from Filters
 * Converts AuditLogFilters to URL query parameters
 *
 * @param filters - Audit log filters
 * @returns URL query string
 */
function buildQueryString(filters: AuditLogFilters): string {
  const params = new URLSearchParams();

  if (filters.page !== undefined) {
    params.append('page', filters.page.toString());
  }

  if (filters.limit !== undefined) {
    params.append('limit', filters.limit.toString());
  }

  if (filters.userId) {
    params.append('userId', filters.userId);
  }

  if (filters.category) {
    if (Array.isArray(filters.category)) {
      filters.category.forEach(cat => params.append('category', cat));
    } else {
      params.append('category', filters.category);
    }
  }

  if (filters.severity) {
    if (Array.isArray(filters.severity)) {
      filters.severity.forEach(sev => params.append('severity', sev));
    } else {
      params.append('severity', filters.severity);
    }
  }

  if (filters.eventType) {
    params.append('eventType', filters.eventType);
  }

  if (filters.endpoint) {
    params.append('endpoint', filters.endpoint);
  }

  if (filters.startDate) {
    params.append('startDate', filters.startDate);
  }

  if (filters.endDate) {
    params.append('endDate', filters.endDate);
  }

  if (filters.resourceType) {
    params.append('resourceType', filters.resourceType);
  }

  if (filters.resourceId) {
    params.append('resourceId', filters.resourceId);
  }

  if (filters.correlationId) {
    params.append('correlationId', filters.correlationId);
  }

  if (filters.ipAddress) {
    params.append('ipAddress', filters.ipAddress);
  }

  if (filters.sortBy) {
    params.append('sortBy', filters.sortBy);
  }

  if (filters.sortOrder) {
    params.append('sortOrder', filters.sortOrder);
  }

  if (filters.cacheStatus && filters.cacheStatus.length > 0) {
    filters.cacheStatus.forEach(status => params.append('cacheStatus', status));
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Fetch Audit Logs
 * Retrieves paginated list of audit logs with optional filters
 *
 * @param filters - Filter parameters for the query
 * @param token - JWT access token for authentication
 * @returns Paginated audit logs
 * @throws {ApiError} If request fails
 */
export async function fetchAuditLogs(
  filters: AuditLogFilters,
  token: string
): Promise<PaginatedAuditLogs> {
  try {
    const queryString = buildQueryString(filters);
    const response = await apiClient.get(`/v1/admin/dashboard${queryString}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend returns { data: [...], pagination: {...} } directly
    // No need to unwrap - response.data is already in the correct format
    const validatedData = PaginatedAuditLogsSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Audit Log Summary
 * Retrieves total count and severity breakdown for the given filters.
 * Call in parallel with fetchAuditLogs — this is the slow query, the other is fast.
 *
 * @param filters - Filter parameters for the query
 * @param token - JWT access token for authentication
 * @returns Audit log summary with total count and severity breakdown
 * @throws {ApiError} If request fails
 */
export async function fetchAuditLogSummary(
  filters: AuditLogFilters,
  token: string
): Promise<AuditLogSummary> {
  try {
    const queryString = buildQueryString(filters);
    const response = await apiClient.get(`/v1/admin/dashboard/summary${queryString}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: { Authorization: `Bearer ${token}` },
    });
    return AuditLogSummarySchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Audit Log by ID
 * Retrieves a single audit log entry by its ID
 *
 * @param id - Audit log entry ID
 * @param token - JWT access token for authentication
 * @returns Single audit log entry
 * @throws {ApiError} If request fails
 */
export async function fetchAuditLogById(id: string, token: string): Promise<AuditLog> {
  try {
    const response = await apiClient.get(`/v1/admin/dashboard/${id}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend wraps response in { message, data } structure
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = AuditLogSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Analytics Summary Response Schema
 * Validates analytics summary response from backend
 */
const AnalyticsSummarySchema = z.object({
  totalEvents: z.number(),
  eventsByCategory: z.record(z.number()),
  eventsBySeverity: z.record(z.number()),
  topUser: z
    .object({
      userId: z.string(),
      count: z.number(),
    })
    .nullable(),
  timeSeries: z.array(
    z.object({
      timestamp: z.string(),
      count: z.number(),
    })
  ),
  topEventTypes: z.array(
    z.object({
      eventType: z.string(),
      count: z.number(),
      category: z.enum([
        'authentication',
        'authorization',
        'user_profile',
        'restaurant',
        'basket',
        'order',
        'payment',
        'loyalty',
        'vehicle',
        'admin',
        'system',
        'product',
      ]),
    })
  ),
  avgResponseTimes: z.array(
    z.object({
      endpoint: z.string(),
      avgMs: z.number(),
      requestCount: z.number(),
      errorRate: z.number(),
    })
  ),
});

/**
 * Fetch Analytics Summary
 * Retrieves analytics summary data for the specified time range
 *
 * @param timeRange - Time range for analytics (1h, 24h, 7d, 30d, 90d)
 * @param token - JWT access token for authentication
 * @param categories - Optional category or array of categories to filter by
 * @returns Analytics summary with metrics and charts data
 * @throws {ApiError} If request fails
 */
export async function fetchAnalyticsSummary(
  timeRange: AnalyticsTimeRange,
  token: string,
  categories?: AuditLogCategory | AuditLogCategory[]
): Promise<AnalyticsSummary> {
  try {
    // Build query string with category filters
    let queryParams = `timeRange=${timeRange}`;

    if (categories) {
      const categoryArray = Array.isArray(categories) ? categories : [categories];
      categoryArray.forEach(category => {
        queryParams += `&category=${category}`;
      });
    }

    const response = await apiClient.get(`/v1/admin/dashboard/analytics/summary?${queryParams}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend returns analytics data directly
    const responseData = response.data;

    // Validate response
    const validatedData = AnalyticsSummarySchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Migration Users Response Schema
 * Validates paginated migration users response
 */
const ListMigrationUsersResponseSchema = z.object({
  data: z.array(
    z.object({
      cognitoId: z.string(),
      email: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      phone: z.string().nullable(),
      isMigrated: z.boolean(),
      createdAt: z.string().nullable(),
      updatedAt: z.string().nullable(),
    })
  ),
  pagination: z.object({
    total: z.number(),
    page: z.coerce.number(),
    limit: z.coerce.number(),
    totalPages: z.number(),
  }),
});

/**
 * Single Migration User Response Schema
 */
const MigrationUserSchema = z.object({
  cognitoId: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
  isMigrated: z.boolean(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

/**
 * Build Query String for Migration Users
 * Converts ListMigrationUsersParams to URL query parameters
 *
 * @param params - Migration users query parameters
 * @returns URL query string
 */
function buildMigrationUsersQueryString(params: ListMigrationUsersParams): string {
  const queryParams = new URLSearchParams();

  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }

  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.search) {
    queryParams.append('search', params.search);
  }

  if (params.isMigrated !== undefined) {
    queryParams.append('isMigrated', params.isMigrated.toString());
  }

  if (params.sortBy) {
    queryParams.append('sortBy', params.sortBy);
  }

  if (params.sortOrder) {
    queryParams.append('sortOrder', params.sortOrder);
  }

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Fetch Migration Users
 * Retrieves paginated list of migration users with optional filters
 *
 * @param params - Query parameters for filtering and pagination
 * @param token - JWT access token for authentication
 * @returns Paginated migration users
 * @throws {ApiError} If request fails
 */
export async function fetchMigrationUsers(
  params: ListMigrationUsersParams,
  token: string
): Promise<ListMigrationUsersResponse> {
  try {
    const queryString = buildMigrationUsersQueryString(params);
    const response = await apiClient.get(`/v1/admin/users-migration${queryString}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend returns { data: [...], pagination: {...} } directly
    const validatedData = ListMigrationUsersResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Migration User by ID
 * Retrieves a single migration user by cognitoId
 *
 * @param cognitoId - Cognito user ID
 * @param token - JWT access token for authentication
 * @returns Single migration user entry
 * @throws {ApiError} If request fails
 */
export async function fetchMigrationUserById(
  cognitoId: string,
  token: string
): Promise<MigrationUser> {
  try {
    const response = await apiClient.get(`/v1/admin/users-migration/${cognitoId}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend wraps response in { message, data } structure
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = MigrationUserSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Create Migration User
 * Creates a new migration user entry
 *
 * @param data - Migration user data
 * @param token - JWT access token for authentication
 * @returns Created migration user
 * @throws {ApiError} If request fails
 */
export async function createMigrationUser(
  data: CreateMigrationUserRequest,
  token: string
): Promise<MigrationUser> {
  try {
    const response = await apiClient.post('/v1/admin/users-migration', data, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend wraps response in { message, data } structure
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = MigrationUserSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Update Migration User
 * Updates an existing migration user
 *
 * @param cognitoId - Cognito user ID
 * @param data - Updated migration user data
 * @param token - JWT access token for authentication
 * @returns Updated migration user
 * @throws {ApiError} If request fails
 */
export async function updateMigrationUser(
  cognitoId: string,
  data: UpdateMigrationUserRequest,
  token: string
): Promise<MigrationUser> {
  try {
    const response = await apiClient.put(`/v1/admin/users-migration/${cognitoId}`, data, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend wraps response in { message, data } structure
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = MigrationUserSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Delete Migration User
 * Deletes a migration user entry
 *
 * @param cognitoId - Cognito user ID
 * @param token - JWT access token for authentication
 * @returns Delete confirmation
 * @throws {ApiError} If request fails
 */
export async function deleteMigrationUser(
  cognitoId: string,
  token: string
): Promise<DeleteMigrationUserResponse> {
  try {
    const response = await apiClient.delete(`/v1/admin/users-migration/${cognitoId}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend returns { message, cognitoId }
    return response.data as DeleteMigrationUserResponse;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * User Management API Functions
 */

/**
 * User Schema
 * Validates user data from backend
 */
const UserLatestDeviceSchema = z.object({
  deviceId: z.string(),
  platform: z.string(),
  appVersion: z.string().nullable(),
  appBuild: z.string().nullable(),
  appVersionId: z.string().nullable(),
  lastSeenAt: z.string(),
});

const UserDeviceSchema = z.object({
  deviceId: z.string(),
  platform: z.string(),
  appVersion: z.string().nullable(),
  appBuild: z.string().nullable(),
  appVersionId: z.string().nullable(),
  userAgent: z.string().nullable(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
});

const UserSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  oloId: z.string().nullable(),
  punchId: z.string().nullable(),
  toastId: z.string().nullable(),
  cognitoId: z.string().nullable(),
  oloAuthTokenIos: z.string().nullable(),
  oloAuthTokenAndroid: z.string().nullable(),
  oloAuthTokenWeb: z.string().nullable(),
  oloAuthTokenBackend: z.string().nullable(),
  punchhAuthToken: z.string().nullable(),
  punchhAccessToken: z.string().nullable(),
  email: z.string().email(),
  phone: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  dateOfBirth: z.string().nullable().optional(),
  termsAccepted: z.boolean(),
  gcmToken: z.string().nullable().optional(),
  apnsToken: z.string().nullable().optional(),
  deviceType: z.string().nullable().optional(),
  deviceId: z.string().nullable().optional(),
  pushNotificationsEnabled: z.boolean().optional(),
  lastTokenUpdate: z.string().nullable().optional(),
  latestDevice: UserLatestDeviceSchema.nullable().optional(),
});

/**
 * User List Response Schema
 * Validates paginated user list response
 */
const UserListResponseSchema = z.object({
  data: z.array(UserSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
});

/**
 * User Details Response Schema
 * Validates single user details response
 */
const UserDetailsResponseSchema = z.object({
  user: UserSchema,
  meta: z.object({
    hasOloAccount: z.boolean(),
    hasPunchhAccount: z.boolean(),
    hasCognitoAccount: z.boolean(),
  }),
  devices: z.array(UserDeviceSchema),
});

/**
 * Platform Token Result Schema
 * Validates per-platform token regeneration result
 */
const PlatformTokenResultSchema = z.object({
  success: z.boolean(),
  token: z.string().optional(),
  error: z.string().optional(),
});

/**
 * Olo Link Response Schema
 * Validates Olo SSO link creation response
 */
const OloLinkResponseSchema = z.object({
  user: UserSchema,
  oloData: z.object({
    authtoken: z.string(),
    provider: z.string(),
    providertoken: z.string(),
  }),
  platformResults: z
    .record(z.enum(['web', 'ios', 'android', 'backend']), PlatformTokenResultSchema)
    .optional(),
});

/**
 * Punchh Account Info Schema
 * Validates Punchh account information response
 * Aligned with /user/loyalty endpoint pattern
 */
const PunchhAccountInfoSchema = z.object({
  linked: z.boolean(),
  userInfo: z
    .object({
      // Primary identifiers
      id: z.number().nullable().optional(),
      userId: z.number().nullable().optional(),
      userCode: z.string().nullable().optional(),

      // Basic profile
      email: z.string().nullable().optional(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      phone: z.string().nullable().optional(),
      birthday: z.string().nullable().optional(),

      // Timestamps
      createdAt: z.string().nullable().optional(),
      updatedAt: z.string().nullable().optional(),
      userJoinedAt: z.string().nullable().optional(),

      // Address
      addressLine1: z.string().nullable().optional(),
      city: z.string().nullable().optional(),
      state: z.string().nullable().optional(),
      zipCode: z.string().nullable().optional(),
    })
    .passthrough() // Allow additional fields from backend sanitized user info
    .nullable(),
  balance: z
    .object({
      accountBalance: z
        .object({
          bankedCurrency: z.number().nullable().optional(),
          bankedCurrencyExpiringOn: z.string().nullable().optional(),
          currentMembershipLevelId: z.number().nullable().optional(),
          currentMembershipLevelName: z.string().nullable().optional(),
          currentMembershipLevelExpiringOn: z.string().nullable().optional(),
          expiringBankedCurrency: z.number().nullable().optional(),
          lifetimePoints: z.number().nullable().optional(),
          redeemablePoints: z.number().nullable().optional(),
          unbankedPoints: z.number().nullable().optional(),
        })
        .nullable()
        .optional(),
      rewards: z
        .array(
          z.object({
            rewardId: z.number().nullable().optional(),
            name: z.string().nullable().optional(),
            description: z.string().nullable().optional(),
            points: z.number().nullable().optional(),
            expiringAt: z.string().nullable().optional(),
          })
        )
        .nullable()
        .optional(),
      coupons: z
        .array(
          z.object({
            code: z.string().nullable().optional(),
            name: z.string().nullable().optional(),
            description: z.string().nullable().optional(),
            imageUrl: z.string().nullable().optional(),
          })
        )
        .nullable()
        .optional(),
      punchCards: z
        .array(
          z.object({
            name: z.string().nullable().optional(),
            description: z.string().nullable().optional(),
            imageUrl: z.string().nullable().optional(),
          })
        )
        .nullable()
        .optional(),
    })
    .passthrough() // Allow additional fields from backend (activeRedemptions, challengeBadges, etc.)
    .nullable(),
});

/**
 * Create Punchh Account Response Schema
 * Validates Punchh account creation response
 */
const CreatePunchhAccountResponseSchema = z.object({
  user: UserSchema,
  punchhData: z.object({
    userId: z.number(),
    authenticationToken: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
  }),
});

/**
 * Build Query String for User List
 * Converts UserListParams to URL query parameters
 *
 * @param params - User list query parameters
 * @returns URL query string
 */
function buildUserQueryString(params: UserListParams): string {
  const queryParams = new URLSearchParams();

  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }

  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.search) {
    queryParams.append('search', params.search);
  }

  if (params.oloLinked !== undefined) {
    queryParams.append('oloLinked', params.oloLinked.toString());
  }

  if (params.punchhLinked !== undefined) {
    queryParams.append('punchhLinked', params.punchhLinked.toString());
  }

  if (params.appVersion) {
    queryParams.append('appVersion', params.appVersion);
  }

  if (params.appBuild) {
    queryParams.append('appBuild', params.appBuild);
  }

  if (params.sort) {
    queryParams.append('sort', params.sort);
  }

  if (params.order) {
    queryParams.append('order', params.order);
  }

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Fetch Users
 * Retrieves paginated list of users with optional filters
 *
 * @param params - Query parameters for filtering and pagination
 * @param token - JWT access token for authentication
 * @returns Paginated users list
 * @throws {ApiError} If request fails
 */
export async function fetchUsers(params: UserListParams, token: string): Promise<UserListResponse> {
  try {
    const queryString = buildUserQueryString(params);
    const response = await apiClient.get(`/v1/admin/users${queryString}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend returns { data: [...], pagination: {...} } directly
    const validatedData = UserListResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch User by ID
 * Retrieves a single user by their ID with metadata
 *
 * @param userId - User ID
 * @param token - JWT access token for authentication
 * @returns User details with metadata
 * @throws {ApiError} If request fails
 */
export async function fetchUserById(userId: string, token: string): Promise<UserDetailsResponse> {
  try {
    const response = await apiClient.get(`/v1/admin/users/${userId}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend returns user details directly
    const validatedData = UserDetailsResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Update User
 * Updates an existing user's information
 *
 * @param userId - User ID
 * @param data - Updated user data
 * @param token - JWT access token for authentication
 * @returns Updated user
 * @throws {ApiError} If request fails
 */
export async function updateUser(
  userId: string,
  data: UpdateUserRequest,
  token: string
): Promise<UpdateUserResponse> {
  try {
    const response = await apiClient.put(`/v1/admin/users/${userId}`, data, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = response.data as { user?: unknown; syncResults?: UpdateUserResponse['syncResults'] };
    const userRaw = responseData.user ?? responseData;
    const validatedUser = UserSchema.parse(userRaw);

    return {
      user: validatedUser,
      syncResults: responseData.syncResults,
    };
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Platform type for Olo token regeneration
 */
export type OloPlatform = 'web' | 'ios' | 'android' | 'backend';

/**
 * Create Olo Link
 * Creates or refreshes Olo SSO link for a user
 *
 * @param userId - User ID
 * @param token - JWT access token for authentication
 * @param platforms - Optional array of platforms to regenerate tokens for. If not specified, regenerates all platforms.
 * @returns Olo link response with updated user and Olo data
 * @throws {ApiError} If request fails
 */
export async function createOloLink(
  userId: string,
  token: string,
  platforms?: OloPlatform[]
): Promise<OloLinkResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/users/${userId}/olo-link`,
      platforms ? { platforms } : {},
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response
    const validatedData = OloLinkResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Punchh Info
 * Retrieves Punchh account information for a user
 *
 * @param userId - User ID
 * @param token - JWT access token for authentication
 * @returns Punchh account info including balance, rewards, and membership
 * @throws {ApiError} If request fails
 */
export async function fetchPunchhInfo(userId: string, token: string): Promise<PunchhAccountInfo> {
  try {
    const response = await apiClient.get(`/v1/admin/users/${userId}/punchh-info`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response
    const validatedData = PunchhAccountInfoSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Create Punchh Account
 * Creates a new Punchh account for a user
 *
 * @param userId - User ID
 * @param token - JWT access token for authentication
 * @returns Punchh account creation response
 * @throws {ApiError} If request fails
 */
export async function createPunchhAccount(
  userId: string,
  token: string
): Promise<CreatePunchhAccountResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/users/${userId}/punchh-account`,
      {},
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response
    const validatedData = CreatePunchhAccountResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Regenerate Punchh Tokens Response Schema
 * Validates Punchh token regeneration response
 */
const RegeneratePunchhTokensResponseSchema = z.object({
  user: UserSchema,
  tokensUpdated: z.object({
    punchhAuthToken: z.boolean(),
    punchhAccessToken: z.boolean(),
  }),
});

/**
 * Regenerate Punchh Tokens
 * Regenerates Punchh auth and access tokens for a user
 *
 * @param userId - User ID
 * @param token - JWT access token for authentication
 * @returns Regenerated tokens response
 * @throws {ApiError} If request fails
 */
export async function regeneratePunchhTokens(
  userId: string,
  token: string
): Promise<RegeneratePunchhTokensResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/users/${userId}/punchh-regenerate`,
      {},
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response
    const validatedData = RegeneratePunchhTokensResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Force Sign-Out Response Schema
 * Validates the response from POST /v1/admin/users/:userId/force-sign-out
 */
const ForceSignOutResponseSchema = z.object({
  message: z.string(),
  idTokenRevocationWritten: z.boolean(),
});

/**
 * Force Sign-Out User
 * Revokes the user's Cognito refresh tokens AND writes a local idToken
 * revocation entry so existing sessions stop authenticating immediately.
 *
 * @param userId - User ID (Cognito sub)
 * @param token - Admin JWT for authentication
 * @returns Force sign-out response
 * @throws {ApiError} If request fails
 */
export async function forceSignOutUser(
  userId: string,
  token: string
): Promise<ForceSignOutResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/users/${userId}/force-sign-out`,
      {},
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return ForceSignOutResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Expire ID Token Response Schema
 * Validates the response from POST /v1/admin/users/:userId/expire-id-token
 */
const ExpireIdTokenResponseSchema = z.object({
  message: z.string(),
  idTokenRevocationWritten: z.boolean(),
});

/**
 * Expire User ID Token
 * Invalidates the user's current idToken via the backend revocation list
 * while leaving their Cognito refresh token valid. Use for testing the
 * FE's silent-refresh flow.
 *
 * @param userId - DB user ID
 * @param token - Admin JWT for authentication
 * @returns Expire ID token response
 * @throws {ApiError} If request fails
 */
export async function expireUserIdToken(
  userId: string,
  token: string
): Promise<ExpireIdTokenResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/users/${userId}/expire-id-token`,
      {},
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return ExpireIdTokenResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Cache Management API Functions
 */

/**
 * Cache Overview Response Schema
 * Validates cache overview response from backend
 */
const CacheOverviewResponseSchema = z.object({
  redis: z.object({
    connected: z.boolean(),
    memoryUsed: z.string(),
    memoryPeak: z.string(),
    totalKeys: z.number(),
    uptime: z.number(),
    version: z.string().optional(),
  }),
  namespaces: z.array(
    z.object({
      name: z.enum([...CACHE_NAMESPACES]),
      displayName: z.string(),
      pattern: z.string(),
      count: z.number(),
      ttlSeconds: z.number().nullable(),
      description: z.string(),
    })
  ),
});

/**
 * Invalidate Response Schema
 * Validates invalidate response from backend
 */
const InvalidateResponseSchema = z.object({
  message: z.string(),
  keysDeleted: z.number(),
});

/**
 * Bulk Invalidate Response Schema
 * Validates bulk invalidate response from backend
 */
const BulkInvalidateResponseSchema = z.object({
  results: z.record(
    z.enum([...CACHE_NAMESPACES]),
    z.object({
      message: z.string(),
      keysDeleted: z.number(),
    })
  ),
  totalKeysDeleted: z.number(),
});

/**
 * Namespace Status Response Schema
 * Validates namespace status response from backend
 */
const NamespaceStatusResponseSchema = z.object({
  namespace: z.enum([...CACHE_NAMESPACES]),
  displayName: z.string(),
  pattern: z.string(),
  count: z.number(),
  ttlSeconds: z.number().nullable(),
  description: z.string(),
});

/**
 * Fetch Cache Overview
 * Retrieves overview of all caches with Redis stats
 *
 * @param token - JWT access token for authentication
 * @returns Cache overview with Redis stats and namespace info
 * @throws {ApiError} If request fails
 */
export async function fetchCacheOverview(token: string): Promise<CacheOverviewResponse> {
  try {
    const response = await apiClient.get('/v1/admin/cache/overview', {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response
    const validatedData = CacheOverviewResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Namespace Status
 * Retrieves status for a specific cache namespace
 *
 * @param namespace - Cache namespace to get status for
 * @param token - JWT access token for authentication
 * @returns Namespace status
 * @throws {ApiError} If request fails
 */
export async function fetchNamespaceStatus(
  namespace: CacheNamespace,
  token: string
): Promise<NamespaceStatusResponse> {
  try {
    const response = await apiClient.get(`/v1/admin/cache/namespaces/${namespace}/status`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response
    const validatedData = NamespaceStatusResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Namespace Keys Response Schema
 * Validates namespace keys list response from backend
 */
const NamespaceKeysResponseSchema = z.object({
  namespace: z.enum([...CACHE_NAMESPACES]),
  keys: z.array(
    z.object({
      key: z.string(),
      cached: z.boolean(),
      ttlSeconds: z.number(),
    })
  ),
  nextCursor: z.string().nullable(),
  totalCount: z.number(),
  hasMore: z.boolean(),
});

/**
 * Fetch Namespace Keys
 * Retrieves paginated list of all keys in a namespace
 *
 * @param namespace - Cache namespace to list keys from
 * @param token - JWT access token for authentication
 * @param cursor - Optional cursor for pagination (default '0')
 * @param limit - Optional limit for keys per page (default 50, max 100)
 * @returns Paginated namespace keys response
 * @throws {ApiError} If request fails
 */
export async function fetchNamespaceKeys(
  namespace: CacheNamespace,
  token: string,
  cursor?: string,
  limit?: number
): Promise<NamespaceKeysResponse> {
  try {
    const queryParams = new URLSearchParams();
    if (cursor) queryParams.append('cursor', cursor);
    if (limit) queryParams.append('limit', limit.toString());

    const queryString = queryParams.toString();
    const url = `/v1/admin/cache/namespaces/${namespace}/keys${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response
    const validatedData = NamespaceKeysResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Invalidate Namespace
 * Invalidates cache for a namespace, optionally filtered by ID
 *
 * @param namespace - Cache namespace to invalidate
 * @param id - Optional ID to filter invalidation (e.g., restaurantId)
 * @param token - JWT access token for authentication
 * @returns Invalidation result
 * @throws {ApiError} If request fails
 */
export async function invalidateNamespace(
  namespace: CacheNamespace,
  token: string,
  id?: string
): Promise<InvalidateResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/cache/namespaces/${namespace}/invalidate`,
      id ? { id } : {},
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response
    const validatedData = InvalidateResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Bulk Invalidate Namespaces
 * Invalidates multiple cache namespaces at once
 *
 * @param namespaces - List of namespaces to invalidate
 * @param token - JWT access token for authentication
 * @returns Bulk invalidation result
 * @throws {ApiError} If request fails
 */
export async function bulkInvalidateNamespaces(
  namespaces: CacheNamespace[],
  token: string
): Promise<BulkInvalidateResponse> {
  try {
    const response = await apiClient.post(
      '/v1/admin/cache/bulk/invalidate',
      { namespaces },
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Validate response
    const validatedData = BulkInvalidateResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Cache Value Response Schema
 * Validates cache value response from backend
 */
const CacheValueResponseSchema = z.object({
  key: z.string(),
  value: z.unknown(),
});

/**
 * Cache Value Response Type
 * The actual cache data for a specific key
 */
export type CacheValueResponse = z.infer<typeof CacheValueResponseSchema>;

/**
 * Fetch Cache Value
 * Retrieves the actual cache value for a specific key
 *
 * @param key - Full cache key (e.g., "fngr:menu:12345:false:pickup")
 * @param token - JWT access token for authentication
 * @returns Cache value response with key and value
 * @throws {ApiError} If request fails or key not found
 */
export async function fetchCacheValue(key: string, token: string): Promise<CacheValueResponse> {
  try {
    const response = await apiClient.get(`/v1/admin/cache/key/${encodeURIComponent(key)}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response
    const validatedData = CacheValueResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Delete Cache Key Response Schema
 * Validates delete cache key response from backend
 */
const DeleteCacheKeyResponseSchema = z.object({
  message: z.string(),
  deleted: z.boolean(),
  key: z.string(),
});

/**
 * Delete Cache Key
 * Deletes a specific cache key
 *
 * @param key - Full cache key to delete (e.g., "fngr:menu:12345:false:pickup")
 * @param token - JWT access token for authentication
 * @returns Delete response with success status
 * @throws {ApiError} If request fails
 */
export async function deleteCacheKey(key: string, token: string): Promise<DeleteCacheKeyResponse> {
  try {
    const response = await apiClient.delete(`/v1/admin/cache/key/${encodeURIComponent(key)}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response
    const validatedData = DeleteCacheKeyResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * App Version Management API Functions
 */

/**
 * App Version Schema
 * Validates app version data from backend
 */
const AppVersionSchema = z.object({
  id: z.string().uuid(),
  appBundleId: z.string(),
  version: z.string(),
  platform: z.enum(['ios', 'android', 'both']),
  isMandatory: z.boolean(),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  minSupportedVersion: z.string().nullable(),
  forceUpdateAfter: z.string().nullable(),
  releaseDate: z.string().nullable(),
  storeUrlIos: z.string().nullable(),
  storeUrlAndroid: z.string().nullable(),
  messageImageUrl: z.string().nullable(),
  messageTitle: z.string().nullable(),
  messageDescription: z.string().nullable(),
  mandatoryMessage: z.string().nullable(),
  actionButtonText: z.string(),
  dismissButtonText: z.string().nullable(),
  releaseNotes: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  isActive: z.boolean(),
  isBeta: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * App Version List Response Schema
 * Validates paginated app version list response from backend
 * Backend returns: { message: string, data: { appVersions: [...], pagination: {...} } }
 */
const AppVersionListResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    appVersions: z.array(AppVersionSchema),
    pagination: z.object({
      total: z.number(),
      count: z.number(),
      limit: z.number(),
      offset: z.number(),
      page: z.number(),
      totalPages: z.number(),
      hasNextPage: z.boolean(),
      hasPreviousPage: z.boolean(),
    }),
  }),
});

/**
 * Delete App Version Response Schema
 * Validates soft delete response
 */
const DeleteAppVersionResponseSchema = z.object({
  message: z.string(),
  id: z.string(),
});

/**
 * Bulk Update App Versions Response Schema
 * Validates bulk update response
 */
const BulkUpdateAppVersionsResponseSchema = z.object({
  message: z.string(),
  updated: z.number(),
  ids: z.array(z.string()),
});

/**
 * Build Query String for App Versions
 * Converts AppVersionListParams to URL query parameters
 *
 * @param params - App version list query parameters
 * @returns URL query string
 */
function buildAppVersionQueryString(
  params: import('@/types/app-version').AppVersionListParams
): string {
  const queryParams = new URLSearchParams();

  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append('offset', params.offset.toString());
  }

  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }

  if (params.platform) {
    queryParams.append('platform', params.platform);
  }

  if (params.isActive !== undefined) {
    queryParams.append('isActive', params.isActive.toString());
  }

  if (params.isMandatory !== undefined) {
    queryParams.append('isMandatory', params.isMandatory.toString());
  }

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Fetch App Versions
 * Retrieves paginated list of app versions with optional filters
 *
 * @param params - Query parameters for filtering and pagination
 * @param token - JWT access token for authentication
 * @returns Paginated app versions list
 * @throws {ApiError} If request fails
 */
export async function fetchAppVersions(
  params: import('@/types/app-version').AppVersionListParams,
  token: string
): Promise<import('@/types/app-version').AppVersionListResponse> {
  try {
    const queryString = buildAppVersionQueryString(params);
    const response = await apiClient.get(`/v1/admin/dashboard/app-versions${queryString}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend returns { message: string, data: { appVersions: [...], pagination: {...} } }
    const validatedData = AppVersionListResponseSchema.parse(response.data);

    // Transform to match frontend expected type
    return {
      data: validatedData.data.appVersions,
      pagination: validatedData.data.pagination,
    };
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch App Version by ID
 * Retrieves a single app version by its ID
 *
 * @param id - App version ID
 * @param token - JWT access token for authentication
 * @returns Single app version
 * @throws {ApiError} If request fails
 */
export async function fetchAppVersionById(
  id: string,
  token: string
): Promise<import('@/types/app-version').AppVersion> {
  try {
    const response = await apiClient.get(`/v1/admin/dashboard/app-versions/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend may wrap response in { data } structure
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = AppVersionSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Create App Version
 * Creates a new app version
 *
 * @param data - App version data
 * @param token - JWT access token for authentication
 * @returns Created app version
 * @throws {ApiError} If request fails
 */
export async function createAppVersion(
  data: import('@/types/app-version').CreateAppVersionRequest,
  token: string
): Promise<import('@/types/app-version').AppVersion> {
  try {
    const response = await apiClient.post('/v1/admin/dashboard/app-versions', data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend may wrap response in { data } structure
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = AppVersionSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Update App Version
 * Updates an existing app version
 *
 * @param id - App version ID
 * @param data - Updated app version data
 * @param token - JWT access token for authentication
 * @returns Updated app version
 * @throws {ApiError} If request fails
 */
export async function updateAppVersion(
  id: string,
  data: import('@/types/app-version').UpdateAppVersionRequest,
  token: string
): Promise<import('@/types/app-version').AppVersion> {
  try {
    const response = await apiClient.put(`/v1/admin/dashboard/app-versions/${id}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Backend may wrap response in { data } structure
    const responseData = (response.data as { data?: unknown }).data || response.data;

    // Validate response
    const validatedData = AppVersionSchema.parse(responseData);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Delete App Version
 * Soft deletes an app version (sets isActive=false)
 *
 * @param id - App version ID
 * @param token - JWT access token for authentication
 * @returns Delete confirmation
 * @throws {ApiError} If request fails
 */
export async function deleteAppVersion(
  id: string,
  token: string
): Promise<import('@/types/app-version').DeleteAppVersionResponse> {
  try {
    const response = await apiClient.delete(`/v1/admin/dashboard/app-versions/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response
    const validatedData = DeleteAppVersionResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Bulk Update App Versions
 * Updates multiple app versions at once (activate/deactivate)
 *
 * @param data - Bulk update request with IDs and isActive status
 * @param token - JWT access token for authentication
 * @returns Bulk update response
 * @throws {ApiError} If request fails
 */
export async function bulkUpdateAppVersions(
  data: import('@/types/app-version').BulkUpdateAppVersionsRequest,
  token: string
): Promise<import('@/types/app-version').BulkUpdateAppVersionsResponse> {
  try {
    const response = await apiClient.patch('/v1/admin/dashboard/app-versions/bulk', data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Validate response
    const validatedData = BulkUpdateAppVersionsResponseSchema.parse(response.data);

    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

// ============================================
// API Keys Management API
// ============================================

/**
 * API Key Schemas
 */
const ApiKeySchema = z.object({
  id: z.string(),
  keyName: z.string(),
  clientType: z.enum([...CLIENT_TYPES]),
  isActive: z.boolean(),
  maxRequests: z.number(),
  windowMs: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastUsed: z.string().nullable(),
  expiresAt: z.string().nullable(),
});

const ApiKeyListResponseSchema = z.object({
  message: z.string(),
  data: z.array(ApiKeySchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

const CreateApiKeyResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    id: z.string(),
    apiKey: z.string(),
    keyName: z.string(),
    clientType: z.enum([...CLIENT_TYPES]),
    maxRequests: z.number(),
    windowMs: z.number(),
    expiresAt: z.string().nullable(),
    createdAt: z.string(),
  }),
});

const RotationHistoryItemSchema = z.object({
  id: z.string(),
  oldKeyId: z.string(),
  newKeyId: z.string(),
  rotationReason: z.string(),
  rotatedAt: z.string(),
  rotatedBy: z.string().nullable(),
});

const RotationStatusResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    id: z.string(),
    keyName: z.string(),
    clientType: z.enum([...CLIENT_TYPES]),
    isActive: z.boolean(),
    expiresAt: z.string().nullable(),
    lastNotifiedAt: z.string().nullable(),
    createdAt: z.string(),
    status: z.enum(['active', 'inactive', 'expired', 'expiring_soon']),
    daysUntilExpiry: z.number().nullable(),
    wasNotified: z.boolean(),
    autoRotationEnabled: z.boolean(),
    rotationHistory: z.array(RotationHistoryItemSchema),
  }),
});

const RotateApiKeyResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    message: z.string(),
    oldKeyId: z.string(),
    newKeyId: z.string(),
    newApiKey: z.string(),
    newKeyName: z.string(),
    clientType: z.enum([...CLIENT_TYPES]),
    rotatedBy: z.string(),
  }),
});

const RotationSummaryResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    total: z.number(),
    web: z.object({
      total: z.number(),
      active: z.number(),
      expired: z.number(),
      expiringSoon: z.number(),
      autoRotationEnabled: z.boolean(),
    }),
    mobile: z.object({
      total: z.number(),
      active: z.number(),
      expired: z.number(),
      expiringSoon: z.number(),
      autoRotationEnabled: z.boolean(),
    }),
    rotations: z.object({
      total: z.number(),
      manual: z.number(),
      automatic: z.number(),
      expired: z.number(),
    }),
  }),
});

/**
 * Build API Key Query String
 */
function buildApiKeyQueryString(params: import('@/types/api-key').ApiKeyListParams): string {
  const queryParams = new URLSearchParams();

  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append('offset', params.offset.toString());
  }

  if (params.clientType) {
    queryParams.append('clientType', params.clientType);
  }

  if (params.isActive !== undefined) {
    queryParams.append('isActive', params.isActive.toString());
  }

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Fetch API Keys
 * Retrieves paginated list of API keys with optional filters
 */
export async function fetchApiKeys(
  params: import('@/types/api-key').ApiKeyListParams,
  token: string
): Promise<import('@/types/api-key').ApiKeyListResponse> {
  try {
    const queryString = buildApiKeyQueryString(params);
    const response = await apiClient.get(`/v1/admin/dashboard/keys${queryString}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = ApiKeyListResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch API Key by ID
 */
export async function fetchApiKeyById(
  keyId: string,
  token: string
): Promise<import('@/types/api-key').ApiKey> {
  try {
    const response = await apiClient.get(`/v1/admin/dashboard/keys/${keyId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = (response.data as { data?: unknown }).data || response.data;
    const validatedData = ApiKeySchema.parse(responseData);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Create API Key
 */
export async function createApiKey(
  data: import('@/types/api-key').CreateApiKeyRequest,
  token: string
): Promise<import('@/types/api-key').CreateApiKeyResponse> {
  try {
    const response = await apiClient.post('/v1/admin/dashboard/keys', data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = CreateApiKeyResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Deactivate API Key
 */
export async function deactivateApiKey(
  keyId: string,
  token: string
): Promise<import('@/types/api-key').DeactivateApiKeyResponse> {
  try {
    const DeactivateApiKeyResponseSchema = z.object({
      message: z.string(),
    });

    const response = await apiClient.delete(`/v1/admin/dashboard/keys/${keyId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = DeactivateApiKeyResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Update API Key Rate Limits
 */
export async function updateApiKeyRateLimits(
  keyId: string,
  data: import('@/types/api-key').UpdateRateLimitsRequest,
  token: string
): Promise<import('@/types/api-key').ApiKey> {
  try {
    const response = await apiClient.patch(`/v1/admin/dashboard/keys/${keyId}/rate-limits`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = (response.data as { data?: unknown }).data || response.data;
    const validatedData = ApiKeySchema.parse(responseData);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Rotate API Key
 */
export async function rotateApiKey(
  keyId: string,
  token: string
): Promise<import('@/types/api-key').RotateApiKeyResponse> {
  try {
    const response = await apiClient.post(`/v1/admin/dashboard/keys/${keyId}/rotate`, null, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = RotateApiKeyResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Rotation Status
 */
export async function fetchRotationStatus(
  keyId: string,
  token: string
): Promise<import('@/types/api-key').RotationStatusResponse> {
  try {
    const response = await apiClient.get(`/v1/admin/dashboard/keys/${keyId}/rotation-status`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = RotationStatusResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Rotation History
 */
export async function fetchRotationHistory(
  keyId: string,
  token: string
): Promise<import('@/types/api-key').RotationHistoryItem[]> {
  try {
    const response = await apiClient.get(`/v1/admin/dashboard/keys/${keyId}/rotation-history`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = (response.data as { data?: unknown }).data || response.data;
    const validatedData = z.array(RotationHistoryItemSchema).parse(responseData);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Rotation Summary
 */
export async function fetchRotationSummary(
  token: string
): Promise<import('@/types/api-key').RotationSummaryResponse> {
  try {
    const response = await apiClient.get('/v1/admin/dashboard/keys/rotation-summary', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = RotationSummaryResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

// ============================================================================
// Global App Config API
// ============================================================================

/**
 * Global Config Response Schema
 * Validates the response from global config endpoints
 */
const GlobalConfigDocumentSchema = z
  .object({
    id: z.string(),
    type: z.string(),
    createdAt: z.number().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough(); // Allow additional properties

const GlobalConfigResponseSchema = z.object({
  count: z.number(),
  documents: z.array(GlobalConfigDocumentSchema),
});

/**
 * Fetch Mobile App Config
 * Retrieves the mobile app configuration from DynamoDB
 *
 * @param token - JWT access token for authentication
 * @returns Global config response with count and documents
 * @throws {ApiError} If request fails
 */
export async function fetchMobileConfig(token: string): Promise<GlobalConfigResponse> {
  try {
    const response = await apiClient.get('/v1/admin/dashboard/config/mobile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = GlobalConfigResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Web CMS Config
 * Retrieves the web CMS configuration from DynamoDB
 *
 * @param token - JWT access token for authentication
 * @returns Global config response with count and documents
 * @throws {ApiError} If request fails
 */
export async function fetchWebConfig(token: string): Promise<GlobalConfigResponse> {
  try {
    const response = await apiClient.get('/v1/admin/dashboard/config/web', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = GlobalConfigResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

// ============================================================================
// Feature Flags API
// ============================================================================

const FeatureFlagDefinitionSchema = z.object({
  scope: z.enum(['global', 'tenant']),
  key: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  // Defaulted so this build still parses a backend that predates the column.
  platformTag: z.enum(PLATFORM_TAGS).default('both'),
  defaultEnabled: z.boolean(),
  isBuiltIn: z.boolean(),
  isArchived: z.boolean(),
  enforced: z.boolean(),
  // Optional so this build still parses a backend that predates the field.
  targetable: z.boolean().optional(),
  ruleCount: z.number(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string(),
});

const FeatureFlagGateSchema = z.object({
  id: z.string(),
  scope: z.enum(['global', 'tenant']),
  flagKey: z.string(),
  platform: z.enum(FEATURE_FLAG_GATE_PLATFORMS),
  versionOp: z.enum(VERSION_OPERATORS).nullable(),
  version: z.string().nullable(),
  enabled: z.boolean(),
  updatedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const FlagGatesResponseSchema = z.object({
  key: z.string(),
  gates: z.array(FeatureFlagGateSchema),
});

const UpsertFlagGateResponseSchema = z.object({
  key: z.string(),
  gate: FeatureFlagGateSchema,
});

const ClearFlagGateResponseSchema = z.object({
  key: z.string(),
  platform: z.enum(FEATURE_FLAG_GATE_PLATFORMS),
});

const FeatureFlagsResponseSchema = z.object({
  flags: z.record(z.string(), z.boolean()),
  definitions: z.array(FeatureFlagDefinitionSchema),
});

const UpdateFeatureFlagResponseSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
});

const ArchiveFeatureFlagResponseSchema = z.object({
  key: z.string(),
  isArchived: z.boolean(),
});

const CreateDefinitionResponseSchema = z.object({
  definition: FeatureFlagDefinitionSchema,
});

const FLAG_SCOPE_PREFIX: Record<FlagScope, string> = {
  global: '/v1/admin/dashboard/feature-flags',
  tenant: '/v1/admin/feature-flags',
};

function authHeaders(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

/**
 * For a DELETE with no body. The shared client sets `Content-Type:
 * application/json` on every request, and Fastify rejects a body-less request
 * that declares one with `FST_ERR_CTP_EMPTY_JSON_BODY` — a 400 raised before
 * the route handler ever runs. Dropping the header is what makes the request
 * reach the backend at all.
 */
function authHeadersNoBody(token: string) {
  return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': undefined } };
}

export async function fetchFeatureFlags(token: string): Promise<FeatureFlagsResponse> {
  try {
    const response = await apiClient.get(FLAG_SCOPE_PREFIX.global, authHeaders(token));
    return FeatureFlagsResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function updateFeatureFlag(
  key: string,
  body: UpdateFeatureFlagBody,
  token: string
): Promise<UpdateFeatureFlagResponse> {
  try {
    const response = await apiClient.patch(
      `${FLAG_SCOPE_PREFIX.global}/${encodeURIComponent(key)}`,
      body,
      authHeaders(token)
    );
    return UpdateFeatureFlagResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function createFeatureFlag(
  scope: FlagScope,
  body: CreateFeatureFlagBody,
  token: string
): Promise<FeatureFlagDefinition> {
  try {
    const response = await apiClient.post(FLAG_SCOPE_PREFIX[scope], body, authHeaders(token));
    return CreateDefinitionResponseSchema.parse(response.data).definition;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function archiveFeatureFlag(
  scope: FlagScope,
  key: string,
  token: string
): Promise<ArchiveFeatureFlagResponse> {
  try {
    const response = await apiClient.delete(
      `${FLAG_SCOPE_PREFIX[scope]}/${encodeURIComponent(key)}`,
      authHeadersNoBody(token)
    );
    return ArchiveFeatureFlagResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

// ============================================================================
// Tenant Feature Flags API
// ============================================================================

const TenantFeatureFlagStateSchema = z.object({
  enabled: z.boolean(),
  configJson: z.record(z.string(), z.unknown()).nullable(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

const TenantFeatureFlagsResponseSchema = z.object({
  tenantKey: z.string(),
  flags: z.record(z.string(), TenantFeatureFlagStateSchema),
  definitions: z.array(FeatureFlagDefinitionSchema),
});

const UpdateTenantFeatureFlagResponseSchema = z.object({
  tenantKey: z.string(),
  featureKey: z.string(),
  enabled: z.boolean(),
  configJson: z.record(z.string(), z.unknown()).nullable(),
});

/**
 * Fetch tenant feature flags for the current backend's tenant.
 */
export async function fetchTenantFeatureFlags(
  token: string
): Promise<TenantFeatureFlagsResponse> {
  try {
    const response = await apiClient.get(FLAG_SCOPE_PREFIX.tenant, authHeaders(token));
    return TenantFeatureFlagsResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Update a single tenant feature flag — its value (`enabled`, `configJson`)
 * and/or its definition (`label`, `description`, `platformTag`). Send only the
 * fields the caller wants to change; omitted fields are preserved on the backend.
 */
export async function updateTenantFeatureFlag(
  key: string,
  body: UpdateTenantFeatureFlagBody,
  token: string
): Promise<UpdateTenantFeatureFlagResponse> {
  try {
    const response = await apiClient.patch(
      `${FLAG_SCOPE_PREFIX.tenant}/${encodeURIComponent(key)}`,
      body,
      authHeaders(token)
    );
    return UpdateTenantFeatureFlagResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

// ============================================================================
// Feature Flag Gates API — per-platform overrides, ≤4 rows per flag
// ============================================================================

export async function fetchFlagGates(
  scope: FlagScope,
  key: string,
  token: string
): Promise<FlagGatesResponse> {
  try {
    const response = await apiClient.get(
      `${FLAG_SCOPE_PREFIX[scope]}/${encodeURIComponent(key)}/gates`,
      authHeaders(token)
    );
    return FlagGatesResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function upsertFlagGate(
  scope: FlagScope,
  key: string,
  platform: FeatureFlagGatePlatform,
  body: UpsertFlagGateBody,
  token: string
): Promise<UpsertFlagGateResponse> {
  try {
    const response = await apiClient.put(
      `${FLAG_SCOPE_PREFIX[scope]}/${encodeURIComponent(key)}/gates/${platform}`,
      body,
      authHeaders(token)
    );
    return UpsertFlagGateResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function clearFlagGate(
  scope: FlagScope,
  key: string,
  platform: FeatureFlagGatePlatform,
  token: string
): Promise<ClearFlagGateResponse> {
  try {
    const response = await apiClient.delete(
      `${FLAG_SCOPE_PREFIX[scope]}/${encodeURIComponent(key)}/gates/${platform}`,
      authHeadersNoBody(token)
    );
    return ClearFlagGateResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Identity targeting rules (FT-5193)
 *
 * A rule decides the flag outright for the people it names — it beats the
 * platform/version gates and the stored value, and unlike a gate it can turn a
 * flag on. Order is fixed by rule type, so the list arrives already sorted and
 * there is nothing to reorder.
 */
const FeatureFlagRuleSchema = z.object({
  id: z.string(),
  scope: z.enum(['global', 'tenant']),
  tenantKey: z.string().nullable(),
  flagKey: z.string(),
  enabled: z.boolean(),
  userIds: z.array(z.string()).nullable(),
  emails: z.array(z.string()).nullable(),
  emailMatch: z.enum(EMAIL_MATCH_MODES),
  isActive: z.boolean(),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string(),
});

const FlagRulesResponseSchema = z.object({
  key: z.string(),
  rules: z.array(FeatureFlagRuleSchema),
});

const FlagRuleResponseSchema = z.object({
  key: z.string(),
  rule: FeatureFlagRuleSchema,
});

const DeleteFlagRuleResponseSchema = z.object({
  key: z.string(),
  ruleId: z.string(),
});

export async function fetchFlagRules(
  scope: FlagScope,
  key: string,
  token: string
): Promise<FlagRulesResponse> {
  try {
    const response = await apiClient.get(
      `${FLAG_SCOPE_PREFIX[scope]}/${encodeURIComponent(key)}/rules`,
      authHeaders(token)
    );
    return FlagRulesResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function createFlagRule(
  scope: FlagScope,
  key: string,
  body: FlagRuleBody,
  token: string
): Promise<FlagRuleResponse> {
  try {
    const response = await apiClient.post(
      `${FLAG_SCOPE_PREFIX[scope]}/${encodeURIComponent(key)}/rules`,
      body,
      authHeaders(token)
    );
    return FlagRuleResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function updateFlagRule(
  scope: FlagScope,
  key: string,
  ruleId: string,
  body: UpdateFlagRuleBody,
  token: string
): Promise<FlagRuleResponse> {
  try {
    const response = await apiClient.patch(
      `${FLAG_SCOPE_PREFIX[scope]}/${encodeURIComponent(key)}/rules/${encodeURIComponent(ruleId)}`,
      body,
      authHeaders(token)
    );
    return FlagRuleResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function deleteFlagRule(
  scope: FlagScope,
  key: string,
  ruleId: string,
  token: string
): Promise<DeleteFlagRuleResponse> {
  try {
    const response = await apiClient.delete(
      `${FLAG_SCOPE_PREFIX[scope]}/${encodeURIComponent(key)}/rules/${encodeURIComponent(ruleId)}`,
      authHeadersNoBody(token)
    );
    return DeleteFlagRuleResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Audit Log Archive Management API Functions
 */

/**
 * Archive Status Response Schema
 * Validates archive status response from backend
 */
const ArchiveStatusResponseSchema = z.object({
  config: z.object({
    s3Bucket: z.string(),
    s3Prefix: z.string(),
    retentionDays: z.number(),
  }),
  archives: z.array(
    z.object({
      year: z.number(),
      month: z.number(),
      key: z.string(),
      exists: z.boolean(),
      lastModified: z.string().optional(),
      size: z.number().optional(),
    })
  ),
  nextScheduledArchive: z
    .object({
      year: z.number(),
      month: z.number(),
    })
    .nullable(),
});

/**
 * Trigger Archive Response Schema
 * Validates trigger archive response from backend
 */
const TriggerArchiveResponseSchema = z.object({
  message: z.string(),
  result: z.object({
    year: z.number(),
    month: z.number(),
    key: z.string(),
    recordCount: z.number(),
    compressedSize: z.number(),
    deletedCount: z.number(),
    dryRun: z.boolean(),
  }),
});

/**
 * Archive Preview Response Schema
 * Validates archive preview response from backend
 */
const ArchivePreviewResponseSchema = z.object({
  year: z.number(),
  month: z.number(),
  estimatedRecordCount: z.number(),
  archiveExists: z.boolean(),
  canArchive: z.boolean(),
  reason: z.string().optional(),
});

/**
 * Fetch Archive Status
 * Retrieves archive status including configuration and archived months
 *
 * @param token - JWT access token for authentication
 * @returns Archive status with config and archived months
 * @throws {ApiError} If request fails
 */
export async function fetchArchiveStatus(token: string): Promise<ArchiveStatusResponse> {
  try {
    const response = await apiClient.get('/v1/admin/dashboard/archive/status', {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = ArchiveStatusResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Fetch Archive Preview
 * Preview what would be archived for a specific month (without triggering)
 *
 * @param year - Year to preview
 * @param month - Month to preview (1-12)
 * @param token - JWT access token for authentication
 * @returns Archive preview with estimated record count and availability
 * @throws {ApiError} If request fails
 */
export async function fetchArchivePreview(
  year: number,
  month: number,
  token: string
): Promise<ArchivePreviewResponse> {
  try {
    const response = await apiClient.get(
      `/v1/admin/dashboard/archive/preview?year=${year}&month=${month}`,
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const validatedData = ArchivePreviewResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Trigger Archive
 * Trigger archive for a specific month
 *
 * @param request - Archive trigger request with year, month, and dryRun flag
 * @param token - JWT access token for authentication
 * @returns Archive result with record count and status
 * @throws {ApiError} If request fails
 */
export async function triggerArchive(
  request: TriggerArchiveRequest,
  token: string
): Promise<TriggerArchiveResponse> {
  try {
    const response = await apiClient.post('/v1/admin/dashboard/archive/trigger', request, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      // Archive operations can take a long time
      timeout: 300000, // 5 minutes
    });

    const validatedData = TriggerArchiveResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

// ============================================
// CSV Import API Functions
// ============================================

/**
 * Submit Import Response Schema
 * Validates the response from submitting a CSV import
 */
const SubmitImportResponseSchema = z.object({
  jobId: z.string().uuid(),
  status: z.literal('pending'),
  totalRows: z.number(),
  message: z.string(),
});

/**
 * Idempotent Import Response Schema
 * Validates response when file was already imported
 */
const IdempotentImportResponseSchema = z.object({
  message: z.string(),
  jobId: z.string().uuid(),
  status: z.enum(['pending', 'processing', 'completed', 'failed', 'cancelled']),
  createdAt: z.string(),
});

/**
 * Import Job Status Response Schema
 * Validates the response from polling job status
 */
const ImportJobStatusResponseSchema = z.object({
  jobId: z.string().uuid(),
  status: z.enum(['pending', 'processing', 'completed', 'failed', 'cancelled']),
  progress: z.object({
    totalRows: z.number(),
    processedRows: z.number(),
    createdRows: z.number(),
    skippedRows: z.number(),
    failedRows: z.number(),
    percentComplete: z.number(),
    estimatedSecondsRemaining: z.number().nullable(),
  }),
  timing: z.object({
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
  }),
  errorMessage: z.string().nullable().optional(),
  failures: z
    .array(
      z.object({
        row: z.number(),
        email: z.string(),
        reason: z.string(),
      })
    )
    .optional(),
});

/**
 * Cancel Import Response Schema
 * Validates the response from cancelling a job
 */
const CancelImportResponseSchema = z.object({
  message: z.string(),
  jobId: z.string().uuid(),
  processedRows: z.number(),
  createdRows: z.number(),
});

/**
 * Force Unstick Import Response Schema
 * Validates the response from force-unsticking a stuck job
 */
const ForceUnstickImportResponseSchema = z.object({
  message: z.string(),
  jobId: z.string().uuid(),
  previousStatus: z.string(),
  processedRows: z.number(),
  createdRows: z.number(),
});

export type ForceUnstickImportResponse = z.infer<typeof ForceUnstickImportResponseSchema>;

/**
 * Submit CSV Import
 * Uploads a CSV file and starts an import job
 *
 * @param file - CSV file to upload
 * @param token - JWT access token for authentication
 * @param onUploadProgress - Optional callback for upload progress (0-100)
 * @returns Submit import response with job ID
 * @throws {ApiError} If upload fails
 */
export async function submitCsvImport(
  file: File,
  token: string,
  onUploadProgress?: (progress: number) => void
): Promise<SubmitImportResponse | IdempotentImportResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/v1/admin/users-migration/import', formData, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': undefined, // Remove default JSON header - axios auto-sets multipart/form-data with boundary
      },
      timeout: 120000, // 2 minutes for large file uploads
      onUploadProgress: progressEvent => {
        if (onUploadProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          onUploadProgress(progress);
        }
      },
    });

    // Check if this is an idempotent response (file already imported)
    if (response.data.createdAt) {
      const validatedData = IdempotentImportResponseSchema.parse(response.data);
      return validatedData;
    }

    // Standard submit response
    const validatedData = SubmitImportResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Get Import Job Status
 * Polls the status of an import job
 *
 * @param jobId - UUID of the import job
 * @param token - JWT access token for authentication
 * @param signal - Optional AbortSignal for cancellation
 * @returns Current job status and progress
 * @throws {ApiError} If request fails
 */
export async function getImportJobStatus(
  jobId: string,
  token: string,
  signal?: AbortSignal
): Promise<ImportJobStatusResponse> {
  try {
    const response = await apiClient.get(`/v1/admin/users-migration/import/${jobId}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });

    const validatedData = ImportJobStatusResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Cancel Import Job
 * Cancels an in-progress or pending import job
 *
 * @param jobId - UUID of the import job to cancel
 * @param token - JWT access token for authentication
 * @returns Cancel response with final counts
 * @throws {ApiError} If cancellation fails
 */
export async function cancelImportJob(jobId: string, token: string): Promise<CancelImportResponse> {
  try {
    const response = await apiClient.delete(`/v1/admin/users-migration/import/${jobId}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const validatedData = CancelImportResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Force Unstick Import Job
 * Force-unsticks a stuck import job that is in pending or processing state
 *
 * @param jobId - UUID of the import job to force-unstick
 * @param token - JWT access token for authentication
 * @param reason - Optional reason for force-unsticking the job
 * @returns Force unstick response with final counts
 * @throws {ApiError} If force-unstick fails
 */
export async function forceUnstickImportJob(
  jobId: string,
  token: string,
  reason?: string
): Promise<ForceUnstickImportResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/users-migration/import/${jobId}/force-unstick`,
      { reason },
      {
        baseURL: ADMIN_API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const validatedData = ForceUnstickImportResponseSchema.parse(response.data);
    return validatedData;
  } catch (error: unknown) {
    handleApiError(error);
  }
}

// ============================================
// Log Debugger Agent API
// ============================================

/**
 * Delete Conversation
 * Removes a log debugger agent conversation from Redis
 *
 * @param conversationId - Conversation ID to delete
 * @param token - JWT access token for authentication
 * @throws {ApiError} If request fails
 */
export async function deleteConversation(conversationId: string, token: string): Promise<void> {
  try {
    await apiClient.delete(`/v1/admin/log-debugger/agent/conversations/${conversationId}`, {
      baseURL: ADMIN_API_BASE_URL,
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error: unknown) {
    handleApiError(error);
  }
}

// ============================================================================
// Admin Dashboard Team Management
// ============================================================================

const TeamRoleSchema = z.enum(['admin', 'manager', 'member']);

const TeamUserSchema = z.object({
  username: z.string(),
  email: z.string(),
  role: TeamRoleSchema,
  status: z.string(),
  enabled: z.boolean(),
  createdAt: z.string().nullable(),
  lastModifiedAt: z.string().nullable(),
});

const ListTeamUsersResponseSchema = z.object({
  data: z.array(TeamUserSchema),
  paginationToken: z.string().nullable(),
  callerRole: TeamRoleSchema,
});

const InviteTeamUserResponseSchema = z.object({
  message: z.string(),
  data: TeamUserSchema,
});

const SimpleMessageResponseSchema = z.object({
  message: z.string(),
});

const UpdateTeamRoleResponseSchema = z.object({
  message: z.string(),
  data: TeamUserSchema,
});

function teamAuthHeaders(token: string) {
  return { Authorization: `Bearer ${token}` };
}

export async function fetchTeamUsers(
  params: import('@/types/team').ListTeamUsersParams,
  token: string
): Promise<import('@/types/team').ListTeamUsersResponse> {
  try {
    const response = await apiClient.get('/v1/admin/dashboard/team', {
      headers: teamAuthHeaders(token),
      params: {
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.paginationToken ? { paginationToken: params.paginationToken } : {}),
        ...(params.search ? { search: params.search } : {}),
      },
    });
    return ListTeamUsersResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function inviteTeamUser(
  body: import('@/types/team').InviteTeamUserRequest,
  token: string
): Promise<import('@/types/team').InviteTeamUserResponse> {
  try {
    const response = await apiClient.post('/v1/admin/dashboard/team/invite', body, {
      headers: teamAuthHeaders(token),
    });
    return InviteTeamUserResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function resendTeamInvite(
  username: string,
  token: string
): Promise<import('@/types/team').SimpleMessageResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/dashboard/team/${encodeURIComponent(username)}/resend-invite`,
      undefined,
      { headers: teamAuthHeaders(token) }
    );
    return SimpleMessageResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function disableTeamUser(
  username: string,
  token: string
): Promise<import('@/types/team').SimpleMessageResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/dashboard/team/${encodeURIComponent(username)}/disable`,
      undefined,
      { headers: teamAuthHeaders(token) }
    );
    return SimpleMessageResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function enableTeamUser(
  username: string,
  token: string
): Promise<import('@/types/team').SimpleMessageResponse> {
  try {
    const response = await apiClient.post(
      `/v1/admin/dashboard/team/${encodeURIComponent(username)}/enable`,
      undefined,
      { headers: teamAuthHeaders(token) }
    );
    return SimpleMessageResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function deleteTeamUser(
  username: string,
  token: string
): Promise<import('@/types/team').SimpleMessageResponse> {
  try {
    const response = await apiClient.delete(
      `/v1/admin/dashboard/team/${encodeURIComponent(username)}`,
      { headers: teamAuthHeaders(token) }
    );
    return SimpleMessageResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

export async function updateTeamUserRole(
  username: string,
  body: import('@/types/team').UpdateTeamRoleRequest,
  token: string
): Promise<import('@/types/team').UpdateTeamRoleResponse> {
  try {
    const response = await apiClient.patch(
      `/v1/admin/dashboard/team/${encodeURIComponent(username)}/role`,
      body,
      { headers: teamAuthHeaders(token) }
    );
    return UpdateTeamRoleResponseSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Group Order Clock
 *
 * A group order expires 14 days after it is created; until then it holds its host's one
 * active-order slot, and a day before the end its host is emailed. These endpoints drive that
 * clock by hand, so QA never waits it out.
 */

const GroupOrderSnapshotSchema = z.object({
  id: z.string(),
  status: z.string(),
  deadlineAt: z.string().nullable(),
  lockedAt: z.string().nullable(),
  version: z.number(),
});

const HostReminderSweepSummarySchema = z.object({
  scanned: z.number(),
  sent: z.number(),
  raced: z.number(),
  errors: z.number(),
  remaining: z.number(),
  dryRun: z.boolean(),
  skippedLocked: z.boolean(),
  skippedDisabled: z.boolean(),
  sampleIds: z.array(z.string()),
});

const ExpirySweepSummarySchema = z.object({
  scanned: z.number(),
  expired: z.number(),
  raced: z.number(),
  contended: z.number(),
  errors: z.number(),
  remaining: z.number(),
  dryRun: z.boolean(),
  skippedLocked: z.boolean(),
  skippedDisabled: z.boolean(),
  sampleIds: z.array(z.string()),
});

/**
 * Expires one group order now: back-dates its deadline, then closes it through the same
 * transition the sweep uses, so the result is indistinguishable from a natural expiry.
 *
 * @throws {ApiError} 404 unknown id, 409 the order is not open, 409 another change holds it
 */
export async function expireGroupOrder(
  groupOrderId: string,
  body: ExpireGroupOrderBody,
  token: string
): Promise<GroupOrderSnapshot> {
  try {
    const response = await apiClient.post(
      `/v1/admin/group-orders/${encodeURIComponent(groupOrderId)}/expire`,
      body,
      authHeaders(token)
    );
    return GroupOrderSnapshotSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/** Closes every group order whose deadline has passed. Run `dryRun` first on a backlog. */
export async function runGroupOrderExpirySweep(
  body: GroupOrderSweepBody,
  token: string
): Promise<ExpirySweepSummary> {
  try {
    const response = await apiClient.post(
      '/v1/admin/group-orders/expire-sweep',
      body,
      authHeaders(token)
    );
    return ExpirySweepSummarySchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Pulls an open order's deadline earlier without expiring it, e.g. into the host reminder
 * window. Earlier only: it can never extend the order.
 *
 * @throws {ApiError} 400 not in the future or not earlier, 404 unknown id, 409 not open or busy
 */
export async function shortenGroupOrderDeadline(
  groupOrderId: string,
  body: ShortenDeadlineBody,
  token: string
): Promise<GroupOrderSnapshot> {
  try {
    const response = await apiClient.post(
      `/v1/admin/group-orders/${encodeURIComponent(groupOrderId)}/deadline`,
      body,
      authHeaders(token)
    );
    return GroupOrderSnapshotSchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/** Emails every host whose open order ends within a day and has not been reminded. */
export async function runGroupOrderHostReminderSweep(
  body: GroupOrderSweepBody,
  token: string
): Promise<HostReminderSweepSummary> {
  try {
    const response = await apiClient.post(
      '/v1/admin/group-orders/host-reminder-sweep',
      body,
      authHeaders(token)
    );
    return HostReminderSweepSummarySchema.parse(response.data);
  } catch (error: unknown) {
    handleApiError(error);
  }
}

/**
 * Export axios instance for custom requests
 * Use this for requests that don't have a dedicated function
 */
export { apiClient };
